/**
 * Parses the raw markdown output from the parse-flight-schedule Edge Function
 * into structured flight pattern rows for preview.
 */

export interface ScheduleHeader {
  valid_from: string;
  valid_to: string;
  schedule_code: string;
}

export interface FlightPatternRow {
  flight_number: string;
  dep_airport: string;
  dep_time: string;
  arr_airport: string;
  arr_time: string;
  days_bitmask: number;
  days_display: string;
  aircraft_type: string;
  notes: string;
  is_overnight: boolean;
}

export interface ValidatedFlightPatternRow extends FlightPatternRow {
  valid: boolean;
  warnings: string[];
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DAY_MAP: Record<string, number> = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tuesday: 2,
  wed: 3, wednesday: 3,
  thu: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
};

/**
 * Convert a days string like "Mon,Wed,Fri" or "Daily" into a bitmask.
 * bit0=Sun, bit1=Mon, ... bit6=Sat
 */
export function daysToBitmask(daysStr: string): number {
  if (!daysStr || daysStr.trim() === '') return 0;
  const trimmed = daysStr.trim().toLowerCase();
  if (trimmed === 'daily') return 0b1111111; // all 7 days

  let mask = 0;
  const parts = trimmed.split(/[,\s/]+/);
  for (const part of parts) {
    const p = part.trim();
    if (p in DAY_MAP) {
      mask |= (1 << DAY_MAP[p]);
    }
  }
  return mask;
}

/**
 * Convert a bitmask back to human-readable day abbreviations.
 */
export function bitmaskToDays(mask: number): string {
  if (mask === 0b1111111) return 'Daily';
  const days: string[] = [];
  for (let i = 0; i < 7; i++) {
    if (mask & (1 << i)) {
      days.push(DAY_NAMES[i]);
    }
  }
  return days.join(', ');
}

/**
 * Extract the JSON header block from the top of the markdown.
 */
export function extractHeader(markdown: string): ScheduleHeader | null {
  // Look for a JSON object on its own line at the top
  const match = markdown.match(/\{[^}]*"valid_from"[^}]*\}/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[0]);
    return {
      valid_from: parsed.valid_from || '',
      valid_to: parsed.valid_to || '',
      schedule_code: parsed.schedule_code || '',
    };
  } catch {
    return null;
  }
}

/**
 * Normalize a time string to HH:MM 24hr format.
 */
function normalizeTime(raw: string): string {
  const cleaned = raw.replace(/\s+/g, '').trim();
  // Match HH:MM or HHMM patterns
  const m = cleaned.match(/^(\d{1,2}):?(\d{2})$/);
  if (m) {
    return `${m[1].padStart(2, '0')}:${m[2]}`;
  }
  return cleaned;
}

/**
 * Parse all markdown tables into FlightPatternRow[].
 * Expected columns: | Flight | Dep | From | To | Arr | Days | Notes |
 */
export function parseFlightTables(markdown: string): FlightPatternRow[] {
  const rows: FlightPatternRow[] = [];
  const lines = markdown.split('\n');

  let currentAircraftType = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Detect section headings for aircraft type
    if (line.startsWith('##')) {
      // Try to extract aircraft type from heading like "## Dash 8" or "## F100 Services"
      const headingText = line.replace(/^#+\s*/, '').trim();
      currentAircraftType = headingText;
      continue;
    }

    // Skip non-table lines
    if (!line.startsWith('|')) continue;

    // Skip separator lines
    if (line.match(/^\|[\s\-:|]+\|$/)) continue;

    const cells = line.split('|').map(c => c.trim()).filter(c => c !== '');

    // Skip header rows - detect by checking if it looks like a header
    if (cells.length >= 5 && (
      cells[0].toLowerCase() === 'flight' ||
      cells[0].toLowerCase() === 'flt' ||
      cells[0].toLowerCase() === 'flight no'
    )) continue;

    // We need at least: Flight, Dep, From, To, Arr
    if (cells.length < 5) continue;

    // Map columns: | Flight | Dep | From | To | Arr | Days | Notes |
    const flight_number = cells[0] || '';
    const dep_time = normalizeTime(cells[1] || '');
    const dep_airport = (cells[2] || '').toUpperCase().substring(0, 3);
    const arr_airport = (cells[3] || '').toUpperCase().substring(0, 3);
    const arr_time = normalizeTime(cells[4] || '');
    const daysStr = cells.length > 5 ? cells[5] : '';
    const notes = cells.length > 6 ? cells[6] : '';

    if (!flight_number || flight_number === '?' || !dep_airport || !arr_airport) continue;

    const days_bitmask = daysToBitmask(daysStr);
    const days_display = bitmaskToDays(days_bitmask);

    // Determine overnight: arrival time < departure time
    const is_overnight = arr_time < dep_time && arr_time !== '' && dep_time !== '';

    // Extract aircraft type from notes if present, otherwise use section heading
    let aircraft = currentAircraftType;
    if (notes) {
      // Look for common aircraft type patterns in notes
      const acMatch = notes.match(/\b(F\d+|DH\d+|Dash\s*\d+|B\d+|CRJ\d*|ATR\d*|Q\d+)\b/i);
      if (acMatch) {
        aircraft = acMatch[1];
      }
    }

    rows.push({
      flight_number,
      dep_airport,
      dep_time,
      arr_airport,
      arr_time,
      days_bitmask,
      days_display,
      aircraft_type: aircraft,
      notes,
      is_overnight,
    });
  }

  return rows;
}

/**
 * Validate parsed rows against existing city codes from Supabase.
 */
export function validateRows(
  rows: FlightPatternRow[],
  validCityCodes: Set<string>
): ValidatedFlightPatternRow[] {
  return rows.map(row => {
    const warnings: string[] = [];

    if (!validCityCodes.has(row.dep_airport)) {
      warnings.push(`dep airport '${row.dep_airport}' not found in cities`);
    }
    if (!validCityCodes.has(row.arr_airport)) {
      warnings.push(`arr airport '${row.arr_airport}' not found in cities`);
    }

    return {
      ...row,
      valid: warnings.length === 0,
      warnings,
    };
  });
}
